import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getHelpArticleBySlug } from '../help/helpArticles';
import { renderMarkdown } from '../help/markdownRenderer';
import { ChevronLeft } from 'lucide-react';

export function HelpArticlePage(): React.ReactElement {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = slug ? getHelpArticleBySlug(slug) : undefined;

  const handleTagClick = (tag: string) => {
    navigate('/help', { state: { searchQuery: tag } });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to="/help"
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Back to Help Center
        </Link>
      </div>

      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/help"
          className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
        >
          Help Center
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-500">/</span>
        <span className="text-gray-900 dark:text-gray-100">
          {article?.title ?? slug ?? 'Article'}
        </span>
      </nav>

      {!article ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Article not found
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            This help article doesn’t exist (or hasn’t been published yet).
          </p>
          <div className="mt-4">
            <Link
              to="/help"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200 font-medium"
            >
              Browse all articles
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <header className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {article.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {article.isPopular && (
                <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/20 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-200">
                  Popular
                </span>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {article.topic}
              </span>
              {article.tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagClick(tag)}
                  className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                >
                  {tag}
                </button>
              ))}
            </div>
          </header>

          <article>{renderMarkdown(article.markdown)}</article>
        </div>
      )}
    </div>
  );
}


